import { Location } from '@angular/common';
import { Compiler, inject, Injector } from '@angular/core';
import {
  ChildrenOutletContexts,
  DefaultTitleStrategy,
  ExtraOptions,
  NavigationBehaviorOptions,
  NavigationExtras,
  Router,
  ROUTER_CONFIGURATION,
  RouteReuseStrategy,
  ROUTES,
  TitleStrategy,
  UrlHandlingStrategy,
  UrlSerializer,
  UrlTree,
} from '@angular/router';
import { getClientConfig } from '@config/client-config';

function flatten<T>(arr: T[][]): T[] {
  return Array.prototype.concat.apply([], arr);
}

function assignExtraOptionsToRouter(opts: ExtraOptions, router: Router): void {
  if (opts.errorHandler) {
    router.errorHandler = opts.errorHandler;
  }

  if (opts.malformedUriErrorHandler) {
    router.malformedUriErrorHandler = opts.malformedUriErrorHandler;
  }

  if (opts.onSameUrlNavigation) {
    router.onSameUrlNavigation = opts.onSameUrlNavigation;
  }

  if (opts.paramsInheritanceStrategy) {
    router.paramsInheritanceStrategy = opts.paramsInheritanceStrategy;
  }

  if (opts.relativeLinkResolution) {
    router.relativeLinkResolution = opts.relativeLinkResolution;
  }

  if (opts.urlUpdateStrategy) {
    router.urlUpdateStrategy = opts.urlUpdateStrategy;
  }

  if (opts.canceledNavigationResolution) {
    router.canceledNavigationResolution = opts.canceledNavigationResolution;
  }
}

function assignRelativeLinkResolution(_router: { relativeLinkResolution: 'legacy' | 'corrected' }): void {}

export function setupGeotabRouter() {
  const urlSerializer = inject(UrlSerializer);
  const contexts = inject(ChildrenOutletContexts);
  const location = inject(Location);
  const injector = inject(Injector);
  const compiler = inject(Compiler);
  const config = inject(ROUTES, { optional: true }) ?? [];
  const opts = inject(ROUTER_CONFIGURATION, { optional: true }) ?? {};
  const defaultTitleStrategy = inject(DefaultTitleStrategy);
  const titleStrategy = inject(TitleStrategy, { optional: true });
  const urlHandlingStrategy = inject(UrlHandlingStrategy, { optional: true });
  const routeReuseStrategy = inject(RouteReuseStrategy, { optional: true });
  const configuration = getClientConfig().configuration;
  // @ts-ignore
  const router = new (class extends Router {
    override navigate(commands: any[], extras?: NavigationExtras): Promise<boolean> {
      return super.navigate(commands, extras);
    }
    override navigateByUrl(url: string | UrlTree, extras?: NavigationBehaviorOptions): Promise<boolean> {
      if (url.toString().includes(configuration.wildcardRoute)) {
        extras = {
          ...extras,
          replaceUrl: true,
        };
      }
      return super.navigateByUrl(url, extras);
    }
    override setBrowserUrl(url: UrlTree, t: any) {
      url.fragment = null;
      // @ts-ignore
      super.setBrowserUrl(url, t);
    }
  })(null, urlSerializer, contexts, location, injector, compiler, flatten(config));

  if (urlHandlingStrategy) {
    router.urlHandlingStrategy = urlHandlingStrategy;
  }

  if (routeReuseStrategy) {
    router.routeReuseStrategy = routeReuseStrategy;
  }

  router.titleStrategy = titleStrategy ?? defaultTitleStrategy;

  // @ts-ignore
  assignExtraOptionsToRouter(opts, router);

  assignRelativeLinkResolution(router);

  return router;
}
